<script>
import ContactAPI from 'dashboard/api/contacts.js';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// Aceleres 2026-08-18: "Enviar contato" do compositor, ao estilo do WhatsApp.
//
// Reaproveita o mesmo endereço da Base de Clientes que o Encaminhar já usa
// (`forward_directory`): a página é de verdade, a busca roda no servidor e o
// mesmo telefone aparece uma única vez. Sem isso, listar contatos aqui voltaria
// a baixar a base inteira de 15 em 15 antes de o atendente poder clicar.
//
// O desenho vem de `contactPickerModal.css`, partilhado com o
// ForwardMessageModal — as duas telas são o mesmo modal, com rodapés
// diferentes.
export default {
  name: 'SendContactModal',
  components: { Avatar },
  props: {
    show: { type: Boolean, default: false },
  },
  emits: ['cancel', 'update:show', 'send'],
  data() {
    return {
      searchQuery: '',
      rawResults: [],
      selectedContacts: [],
      isSearching: false,
      isSending: false,
      searchTimeout: null,
      searchRequestId: 0,
      searchFailed: false,
      hasMoreContacts: false,
      nextPage: 2,
      copy: {
        dialog: 'Enviar contatos',
        close: 'Fechar',
        title: 'Enviar contatos',
        searchPlaceholder: 'Buscar um nome ou número',
        loading: 'Carregando contatos…',
        loadError: 'Não foi possível carregar os contatos.',
        empty: 'Nenhum contato encontrado.',
        send: 'Enviar',
        recent: 'Contatos',
        results: 'Resultados',
        noPhone: 'Sem telefone cadastrado',
      },
    };
  },
  computed: {
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
    listHeading() {
      return this.searchQuery.trim() ? this.copy.results : this.copy.recent;
    },
    canSend() {
      return (
        this.selectedContacts.length > 0 &&
        this.selectedContacts.every(contact => this.contactPhone(contact)) &&
        !this.isSending
      );
    },
    selectedContactNames() {
      return this.selectedContacts
        .map(contact => this.contactName(contact))
        .join(', ');
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.resetState();
        this.loadContacts('');
        this.$nextTick(() => this.$refs.searchInput?.focus());
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.searchTimeout);
  },
  methods: {
    resetState() {
      clearTimeout(this.searchTimeout);
      this.searchQuery = '';
      this.rawResults = [];
      this.selectedContacts = [];
      this.isSearching = false;
      this.isSending = false;
      this.searchFailed = false;
      this.hasMoreContacts = false;
      this.nextPage = 2;
      this.searchRequestId += 1;
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchRequestId += 1;
      this.rawResults = [];
      this.isSearching = true;
      this.searchFailed = false;
      this.hasMoreContacts = false;
      this.nextPage = 2;
      const query = this.searchQuery.trim();
      this.searchTimeout = setTimeout(
        () => this.loadContacts(query),
        query ? 250 : 0
      );
    },
    extractContacts(data) {
      const payload = data?.payload;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.contacts)) return payload.contacts;
      return [];
    },
    appendUniqueContacts(currentContacts, newContacts) {
      const contactsById = new Map(
        currentContacts.map(contact => [contact.id, contact])
      );

      newContacts.forEach(contact => {
        if (contact?.id != null) contactsById.set(contact.id, contact);
      });

      return Array.from(contactsById.values());
    },
    async loadContacts(query, { append = false } = {}) {
      const requestId = append
        ? this.searchRequestId
        : this.searchRequestId + 1;
      this.searchRequestId = requestId;
      this.isSearching = true;
      this.searchFailed = false;

      const page = append ? this.nextPage : 1;

      try {
        const { data } = await ContactAPI.getForwardDirectory(page, query);
        if (requestId !== this.searchRequestId) return;

        const contacts = this.extractContacts(data);
        this.rawResults = append
          ? this.appendUniqueContacts(this.rawResults, contacts)
          : contacts;

        this.hasMoreContacts = Boolean(data?.meta?.has_more);
        this.nextPage = page + 1;
      } catch {
        if (requestId !== this.searchRequestId) return;
        if (!append) {
          this.rawResults = [];
          this.searchFailed = true;
        }
        this.hasMoreContacts = false;
      } finally {
        if (requestId === this.searchRequestId) this.isSearching = false;
      }
    },
    onListScroll(event) {
      if (!this.hasMoreContacts || this.isSearching) return;

      const { scrollTop, clientHeight, scrollHeight } = event.target;
      if (scrollTop + clientHeight < scrollHeight - 240) return;

      this.loadContacts(this.searchQuery.trim(), { append: true });
    },
    contactName(record) {
      return (
        record?.name ||
        record?.phone_number ||
        record?.phoneNumber ||
        `Contato #${record?.id}`
      );
    },
    contactThumb(record) {
      return record?.thumbnail || record?.avatar_url || record?.avatarUrl || '';
    },
    contactPhone(record) {
      return record?.phone_number || record?.phoneNumber || '';
    },
    isSelected(record) {
      return this.selectedContacts.some(contact => contact.id === record?.id);
    },
    toggleContact(record) {
      this.selectedContacts = this.isSelected(record)
        ? this.selectedContacts.filter(contact => contact.id !== record.id)
        : [...this.selectedContacts, record];
    },
    handleCancel() {
      if (this.isSending) return;
      this.localShow = false;
      this.$emit('cancel');
    },
    handleSend() {
      if (!this.canSend) return;

      this.isSending = true;
      this.$emit(
        'send',
        this.selectedContacts.map(contact => ({
          contact_id: contact.id,
          name: this.contactName(contact),
          phone_number: this.contactPhone(contact),
          thumbnail: this.contactThumb(contact),
        }))
      );
      this.isSending = false;
      this.localShow = false;
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <Transition name="acl-fwd-fade">
      <div v-if="show" class="acl-forward-overlay" @click.self="handleCancel">
        <div
          class="acl-forward-modal acl-cs-modal acl-forward-modal--contact-send"
          role="dialog"
          aria-modal="true"
          :aria-label="copy.dialog"
        >
          <div class="acl-forward-header">
            <button
              type="button"
              class="acl-forward-close"
              :aria-label="copy.close"
              @click="handleCancel"
            >
              <span class="i-lucide-x" />
            </button>
            <div class="acl-forward-header__titles">
              <h2 class="acl-forward-title">{{ copy.title }}</h2>
            </div>
          </div>

          <div class="acl-forward-search">
            <span class="i-lucide-search acl-forward-search__icon" />
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="acl-forward-search__input"
              :placeholder="copy.searchPlaceholder"
              autocomplete="off"
              @input="onSearchInput"
            />
            <span
              v-if="isSearching"
              class="i-lucide-loader acl-forward-search__spinner"
            />
          </div>

          <div class="acl-forward-list" @scroll.passive="onListScroll">
            <div class="acl-forward-list__heading">{{ listHeading }}</div>
            <div
              v-if="isSearching && rawResults.length === 0"
              class="acl-forward-empty"
            >
              <span class="i-lucide-loader acl-forward-empty__spinner" />
              {{ copy.loading }}
            </div>
            <div v-else-if="searchFailed" class="acl-forward-empty">
              {{ copy.loadError }}
            </div>
            <div v-else-if="rawResults.length === 0" class="acl-forward-empty">
              {{ copy.empty }}
            </div>
            <div
              v-for="contactRecord in rawResults"
              :key="contactRecord.id"
              class="acl-forward-row"
            >
              <button
                type="button"
                class="acl-forward-item"
                :class="{
                  'acl-forward-item--selected': isSelected(contactRecord),
                }"
                :aria-pressed="isSelected(contactRecord)"
                @click="toggleContact(contactRecord)"
              >
                <span
                  class="acl-forward-check"
                  :class="{
                    'acl-forward-check--on': isSelected(contactRecord),
                  }"
                  aria-hidden="true"
                >
                  <span
                    v-if="isSelected(contactRecord)"
                    class="i-lucide-check"
                  />
                </span>
                <Avatar
                  :name="contactName(contactRecord)"
                  :src="contactThumb(contactRecord)"
                  :size="52"
                  rounded-full
                />
                <div class="acl-forward-item__info">
                  <span class="acl-forward-item__name">
                    {{ contactName(contactRecord) }}
                  </span>
                  <span
                    v-if="!contactPhone(contactRecord)"
                    class="acl-forward-item__secondary"
                  >
                    {{ copy.noPhone }}
                  </span>
                </div>
              </button>
            </div>
          </div>

          <div v-if="selectedContacts.length" class="acl-forward-footer">
            <div class="acl-forward-actionbar w-full">
              <span
                class="acl-forward-actionbar__names"
                :title="selectedContactNames"
              >
                {{ selectedContactNames }}
              </span>
              <button
                type="button"
                class="acl-forward-send"
                :class="{ 'acl-forward-send--disabled': !canSend }"
                :disabled="!canSend"
                :aria-label="copy.send"
                @click="handleSend"
              >
                <span class="i-lucide-send-horizontal" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped src="./contactPickerModal.css"></style>

<style scoped>
.acl-cs-modal {
  max-width: 500px;
  height: min(92dvh, 800px);
  max-height: calc(100dvh - 32px);
}

.acl-cs-modal .acl-forward-header {
  gap: 12px;
  min-height: 64px;
  padding: 8px 16px;
  border-bottom: none;
}

.acl-cs-modal .acl-forward-header__titles {
  flex: 1 1 auto;
  min-width: 0;
}

.acl-cs-modal .acl-forward-title {
  font-size: 18px;
}

.acl-cs-modal .acl-forward-subtitle {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.acl-cs-modal .acl-cs-advance {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 18px;
  background: #00a884;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.acl-cs-modal .acl-cs-advance--disabled,
.acl-cs-modal .acl-cs-advance:disabled {
  background: #e9edef;
  color: #8696a0;
  cursor: not-allowed;
}

.acl-cs-modal .acl-forward-search {
  min-height: 50px;
  margin: 4px 20px 12px;
  border-color: #111b21;
  border-radius: 25px;
}

.acl-cs-modal .acl-forward-search__input {
  font-size: 16px;
}

.acl-cs-modal .acl-forward-list {
  padding: 8px 0 calc(14px + env(safe-area-inset-bottom));
}

.acl-cs-modal .acl-forward-list__heading {
  padding: 14px 24px 10px;
}

.acl-cs-modal .acl-forward-row {
  padding: 0 12px;
}

.acl-cs-modal .acl-forward-item {
  min-height: 76px;
  gap: 14px;
  padding: 10px 16px;
  border-radius: 12px;
}

.acl-cs-modal .acl-forward-item--selected {
  background: #f0f2f5;
}

.acl-cs-modal .acl-forward-item--selected:hover {
  background: #e9edef;
}

.acl-cs-modal .acl-forward-check {
  order: 0;
  margin-left: 0;
}

:global(.dark) .acl-cs-modal .acl-cs-advance--disabled,
:global(.dark) .acl-cs-modal .acl-cs-advance:disabled {
  background: #2a3942;
  color: #8696a0;
}

:global(.dark) .acl-cs-modal .acl-forward-item--selected {
  background: #2a3942;
}

:global(.dark) .acl-cs-modal .acl-forward-item--selected:hover {
  background: #34454f;
}

@media (max-width: 767px) {
  .acl-cs-modal {
    width: 100%;
    max-width: none;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }

  .acl-cs-modal .acl-forward-header {
    min-height: 60px;
    padding: 6px 12px 10px;
  }

  .acl-cs-modal .acl-forward-close {
    width: 40px;
    height: 40px;
    border: 1px solid #d5dbdc;
    background: #f0f2f5;
    color: #111b21;
  }

  .acl-cs-modal .acl-forward-close > span {
    width: 20px;
    height: 20px;
  }

  .acl-cs-modal .acl-forward-title {
    font-size: 17px;
  }

  .acl-cs-modal .acl-forward-subtitle {
    font-size: 12px;
  }

  .acl-cs-modal .acl-forward-search {
    margin: 2px 12px 4px;
    border: none;
    background: #f0f2f5;
  }

  .acl-cs-modal .acl-forward-search:focus-within {
    box-shadow: 0 0 0 1px #00a884;
  }

  :global(.dark) .acl-cs-modal .acl-forward-close {
    border-color: #3b4a54;
    background: #2a3942;
    color: #e9edef;
  }

  :global(.dark) .acl-cs-modal .acl-forward-search {
    background: #2a3942;
  }
}
</style>
